import { useState, useEffect, useCallback } from 'react';
import type {
  EditionSummaryResponse,
  EditionSourcesResponse,
  EditionScoringResponse,
  EditionClustersResponse,
  EditionRunsResponse,
} from '../types';
import { editionApi as api } from '../api';
import { EditionHeader } from './EditionHeader';
import { PipelineStats } from './PipelineStats';
import { CategoryReportCard } from './CategoryReportCard';
import { PipelineRunsTable } from './PipelineRunsTable';
import { SourceWeightsPanel } from './SourceWeightsPanel';
import { ClusterAnalysis } from './ClusterAnalysis';
import { ScoringBreakdown } from './ScoringBreakdown';
import { CategoryFilter } from './CategoryFilter';
import './StatusPage.css';

type TabType = 'overview' | 'sources' | 'scoring' | 'selection' | 'history';

interface TabData<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  loaded: boolean;
}

export function StatusPage() {
  // Core edition data (lightweight, always loaded)
  const [summary, setSummary] = useState<EditionSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tab-specific data (lazy loaded)
  const [sourcesTab, setSourcesTab] = useState<TabData<EditionSourcesResponse>>({
    data: null, loading: false, error: null, loaded: false
  });
  const [scoringTab, setScoringTab] = useState<TabData<EditionScoringResponse>>({
    data: null, loading: false, error: null, loaded: false
  });
  const [clustersTab, setClustersTab] = useState<TabData<EditionClustersResponse>>({
    data: null, loading: false, error: null, loaded: false
  });
  const [runsTab, setRunsTab] = useState<TabData<EditionRunsResponse>>({
    data: null, loading: false, error: null, loaded: false
  });

  // UI state
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);

  // Load initial summary
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.getEditionSummary();
        setSummary(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch edition data');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  // Lazy load sources data
  const loadSources = useCallback(async () => {
    if (!summary || sourcesTab.loaded || sourcesTab.loading) return;

    setSourcesTab(prev => ({ ...prev, loading: true, error: null }));
    try {
      const data = await api.getEditionSources(summary.date, summary.edition);
      setSourcesTab({ data, loading: false, error: null, loaded: true });
    } catch (err) {
      setSourcesTab(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load sources'
      }));
    }
  }, [summary, sourcesTab.loaded, sourcesTab.loading]);

  // Lazy load scoring data
  const loadScoring = useCallback(async () => {
    if (!summary || scoringTab.loaded || scoringTab.loading) return;

    setScoringTab(prev => ({ ...prev, loading: true, error: null }));
    try {
      const data = await api.getEditionScoring(summary.date, summary.edition);
      setScoringTab({ data, loading: false, error: null, loaded: true });
    } catch (err) {
      setScoringTab(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load scoring'
      }));
    }
  }, [summary, scoringTab.loaded, scoringTab.loading]);

  // Lazy load clusters data
  const loadClusters = useCallback(async () => {
    if (!summary || clustersTab.loaded || clustersTab.loading) return;

    setClustersTab(prev => ({ ...prev, loading: true, error: null }));
    try {
      const data = await api.getEditionClusters(summary.date, summary.edition);
      setClustersTab({ data, loading: false, error: null, loaded: true });
    } catch (err) {
      setClustersTab(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load clusters'
      }));
    }
  }, [summary, clustersTab.loaded, clustersTab.loading]);

  // Lazy load runs data
  const loadRuns = useCallback(async () => {
    if (!summary || runsTab.loaded || runsTab.loading) return;

    setRunsTab(prev => ({ ...prev, loading: true, error: null }));
    try {
      const data = await api.getEditionRuns(summary.date, summary.edition);
      setRunsTab({ data, loading: false, error: null, loaded: true });
    } catch (err) {
      setRunsTab(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load runs'
      }));
    }
  }, [summary, runsTab.loaded, runsTab.loading]);

  // Load data when tab changes
  useEffect(() => {
    switch (activeTab) {
      case 'sources':
        loadSources();
        break;
      case 'scoring':
        loadScoring();
        break;
      case 'selection':
        loadClusters();
        break;
      case 'history':
        loadRuns();
        break;
    }
  }, [activeTab, loadSources, loadScoring, loadClusters, loadRuns]);

  const handleRefresh = () => {
    // Reset all state
    setSummary(null);
    setLoading(true);
    setError(null);
    setSourcesTab({ data: null, loading: false, error: null, loaded: false });
    setScoringTab({ data: null, loading: false, error: null, loaded: false });
    setClustersTab({ data: null, loading: false, error: null, loaded: false });
    setRunsTab({ data: null, loading: false, error: null, loaded: false });

    const fetchSummary = async () => {
      try {
        const response = await api.getEditionSummary();
        setSummary(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch edition data');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  };

  const renderTabLoading = (tabName: string) => (
    <div className="status-page__tab-loading">
      <div className="status-page__loading-spinner" />
      <p>Loading {tabName} data...</p>
    </div>
  );

  const renderTabError = (error: string, onRetry: () => void) => (
    <div className="status-page__tab-error">
      <p>{error}</p>
      <button className="status-page__retry-button" onClick={onRetry} type="button">
        Retry
      </button>
    </div>
  );

  if (loading) {
    return (
      <main id="main-content" className="status-page">
        <div className="container">
          <div className="status-page__loading">
            <div className="status-page__loading-spinner" />
            <p>Loading pipeline status...</p>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main id="main-content" className="status-page">
        <div className="container">
          <div className="status-page__error">
            <h3 className="status-page__error-headline">Unable to Load Status</h3>
            <p className="status-page__error-text">{error}</p>
            <button
              className="status-page__retry-button"
              onClick={handleRefresh}
              type="button"
            >
              Try Again
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <main id="main-content" className="status-page">
      <div className="container">
        <EditionHeader
          date={summary.date}
          edition={summary.edition}
          generatedAt={summary.generatedAt}
        />

        <div className="status-page__refresh">
          <button
            className="status-page__refresh-button"
            onClick={handleRefresh}
            type="button"
          >
            ↻ Refresh
          </button>
        </div>

        {/* Tab Navigation */}
        <nav className="status-page__tabs">
          <button
            className={`status-page__tab ${activeTab === 'overview' ? 'status-page__tab--active' : ''}`}
            onClick={() => setActiveTab('overview')}
            type="button"
          >
            Overview
          </button>
          <button
            className={`status-page__tab ${activeTab === 'sources' ? 'status-page__tab--active' : ''}`}
            onClick={() => setActiveTab('sources')}
            type="button"
          >
            Sources
          </button>
          <button
            className={`status-page__tab ${activeTab === 'scoring' ? 'status-page__tab--active' : ''}`}
            onClick={() => setActiveTab('scoring')}
            type="button"
          >
            Scoring
          </button>
          <button
            className={`status-page__tab ${activeTab === 'selection' ? 'status-page__tab--active' : ''}`}
            onClick={() => setActiveTab('selection')}
            type="button"
          >
            Selection
          </button>
          <button
            className={`status-page__tab ${activeTab === 'history' ? 'status-page__tab--active' : ''}`}
            onClick={() => setActiveTab('history')}
            type="button"
          >
            History
          </button>
        </nav>

        {/* Category Filter - Show for sources, scoring, and selection tabs */}
        {(activeTab === 'sources' || activeTab === 'scoring' || activeTab === 'selection') && (
          <CategoryFilter
            categories={summary.categories}
            selectedCategory={selectedCategory}
            selectedSubCategory={selectedSubCategory}
            onCategoryChange={setSelectedCategory}
            onSubCategoryChange={setSelectedSubCategory}
          />
        )}

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
            <section className="status-page__section">
              <h2 className="status-page__section-title">Pipeline Overview</h2>
              <PipelineStats categories={summary.categories} />
            </section>

            <section className="status-page__section">
              <h2 className="status-page__section-title">Category Reports</h2>
              <div className="status-page__reports">
                {summary.categories.map((category) => {
                  const key = category.subCategorySlug
                    ? `${category.categorySlug}-${category.subCategorySlug}`
                    : category.categorySlug;
                  return <CategoryReportCard key={key} category={category} />;
                })}
              </div>
            </section>
          </>
        )}

        {activeTab === 'sources' && (
          <section className="status-page__section">
            {sourcesTab.loading && renderTabLoading('sources')}
            {sourcesTab.error && renderTabError(sourcesTab.error, () => {
              setSourcesTab(prev => ({ ...prev, loaded: false }));
              loadSources();
            })}
            {sourcesTab.data && (
              <SourceWeightsPanel
                categories={sourcesTab.data.categories}
                selectedCategory={selectedCategory}
                selectedSubCategory={selectedSubCategory}
              />
            )}
          </section>
        )}

        {activeTab === 'scoring' && (
          <section className="status-page__section">
            {scoringTab.loading && renderTabLoading('scoring')}
            {scoringTab.error && renderTabError(scoringTab.error, () => {
              setScoringTab(prev => ({ ...prev, loaded: false }));
              loadScoring();
            })}
            {scoringTab.data && (
              <ScoringBreakdown
                categories={scoringTab.data.categories}
                selectedCategory={selectedCategory}
                selectedSubCategory={selectedSubCategory}
              />
            )}
          </section>
        )}

        {activeTab === 'selection' && (
          <section className="status-page__section">
            {clustersTab.loading && renderTabLoading('clusters')}
            {clustersTab.error && renderTabError(clustersTab.error, () => {
              setClustersTab(prev => ({ ...prev, loaded: false }));
              loadClusters();
            })}
            {clustersTab.data && (
              <ClusterAnalysis
                categories={clustersTab.data.categories}
                selectedCategory={selectedCategory}
                selectedSubCategory={selectedSubCategory}
              />
            )}
          </section>
        )}

        {activeTab === 'history' && (
          <section className="status-page__section">
            <h2 className="status-page__section-title">Pipeline Execution History</h2>
            {runsTab.loading && renderTabLoading('history')}
            {runsTab.error && renderTabError(runsTab.error, () => {
              setRunsTab(prev => ({ ...prev, loaded: false }));
              loadRuns();
            })}
            {runsTab.data && (
              <PipelineRunsTable
                stages={runsTab.data.stages}
                categories={runsTab.data.categories}
              />
            )}
          </section>
        )}
      </div>
    </main>
  );
}
